import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaEye, FaPlay, FaSearch, FaFilter, FaCheck, FaTimes, FaSpinner, FaCamera, FaVideo, FaUser, FaClock, FaGlobe } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const FreeRunningAds = () => {
  const [runningAds, setRunningAds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showScreenshotModal, setShowScreenshotModal] = useState(false);
  const [currentScreenshot, setCurrentScreenshot] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectLoading, setRejectLoading] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [showApproveSpinner, setShowApproveSpinner] = useState(false);
  const [showRejectSpinner, setShowRejectSpinner] = useState(false);
  const [previewMedia, setPreviewMedia] = useState(null);
  const navigate = useNavigate();

  // Media preview functions
  const openMediaPreview = (mediaType, url) => {
    setPreviewMedia({
      type: mediaType,
      url: url
    });
  };

  const closeMediaPreview = () => {
    setPreviewMedia(null);
  };

  // Accept ad proof
  const handleAcceptSubmit = async (adId, reporterId) => {
    console.log("Accepting ad proof for Ad ID:", adId, "Reporter ID:", reporterId);
    setShowApproveSpinner(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/admin/accept/free/ad/proof`,
        { adId, reporterId },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      toast.success("Ad proof accepted successfully!");
      setTimeout(() => window.location.reload(), 1200);
    } catch (err) {
      toast.error("Failed to accept ad proof. Try again.");
    } finally {
      setShowApproveSpinner(false);
    }
  };

  // Reject ad proof
  const handleRejectSubmit = async () => {
    if (!rejectReason.trim() || !rejectTarget) return;
    setRejectLoading(true);
    setShowRejectSpinner(true);
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/admin/reject/free/ad/proof`,
        {
          adId: rejectTarget.adId,
          reporterId: rejectTarget.reporterId,
          rejectNote: rejectReason,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      console.log("Ad proof rejected successfully", res);
      toast.success("Ad proof rejected successfully!");
      setShowRejectModal(false);
      setRejectReason("");
      setRejectTarget(null);
      setTimeout(() => window.location.reload(), 1200);
    } catch (err) {
      toast.error("Failed to reject ad. Try again.");
    } finally {
      setRejectLoading(false);
      setShowRejectSpinner(false);
    }
  };

  useEffect(() => {
    const fetchRunningAds = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/admin/get/running/free/ad`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        console.log("Response of  free Running Ads:", response);
        const adsData = Array.isArray(response.data.data) ? response.data.data : [];
        console.log("🔍 Debug - Running ads data:", adsData);
        console.log("🔍 Debug - First ad sample:", adsData[0]);
        setRunningAds(adsData);
      } catch (err) {
        setError("Failed to fetch running ads");
      } finally {
        setLoading(false);
      }
    };
    fetchRunningAds();
  }, []);

  if (loading) return <div>Loading...</div>;
  if (error) return <div className="text-red-500">{error}</div>;

  // Filter logic: search all fields
  const filteredAds = runningAds.filter((ad) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.trim().toLowerCase();
    return (
      (ad.adId && ad.adId.toString().toLowerCase().includes(term)) ||
      (ad.reporterId && ad.reporterId.toString().toLowerCase().includes(term)) ||
      (ad.iinsafId && ad.iinsafId.toString().toLowerCase().includes(term)) ||
      (ad.channelName && ad.channelName.toLowerCase().includes(term)) ||
      (ad.duration && ad.duration.toString().toLowerCase().includes(term))
    );
  });

  // Pagination logic
  // Group ads by adId
  const groupedAds = filteredAds.reduce((acc, ad) => {
    const adId = ad.adId;
    if (!acc[adId]) {
      // Determine userType based on iinsafId prefix or ad.userType
      let userType = 'reporter'; // default
      if (ad.userType === 'influencer' || (ad.iinsafId && ad.iinsafId.startsWith('INF'))) {
        userType = 'influencer';
      }
      console.log("🔍 Debug - Ad userType determination:", {
        adId: ad.adId,
        adUserType: ad.userType,
        iinsafId: ad.iinsafId,
        determinedUserType: userType
      });

      acc[adId] = {
        adId: adId,
        requiredReporterCount: ad.requiredReportersCount,
        userType: userType, // Add userType to grouped ad
        reporters: []
      };
    }
    acc[adId].reporters.push(ad);
    return acc;
  }, {});
  const groupedAdsArray = Object.values(groupedAds).reverse();
  const totalRows = groupedAdsArray.length;
  const totalPages = Math.ceil(totalRows / rowsPerPage);
  const paginatedAds = groupedAdsArray.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <React.Fragment>
      {/* Approve Spinner Modal */}
      {showApproveSpinner && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4 font-sans">
          <div className="flex flex-col items-center p-6 sm:p-8 bg-white rounded-2xl shadow-2xl border border-blue-200 animate-fadeIn max-w-xs sm:max-w-md w-full">
            <div className="relative mb-4 w-12 h-12 sm:w-16 sm:h-16 grid place-items-center">
              <span className="absolute inset-0 rounded-full border-4 border-transparent border-r-[#25b09b] animate-spin" />
              <span className="absolute inset-0 m-1 rounded-full border-4 border-transparent border-r-[#25b09b] animate-spin-slow" />
            </div>
            <span className="text-base sm:text-lg font-bold text-slate-800 text-center uppercase tracking-wider">
              Approving Proof...
            </span>
            <span className="text-[10px] sm:text-sm text-slate-500 mt-2 text-center uppercase font-medium">Please wait a moment</span>
          </div>
        </div>
      )}

      {/* Reject Spinner Modal */}
      {showRejectSpinner && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4 font-sans">
          <div className="flex flex-col items-center p-6 sm:p-8 bg-white rounded-2xl shadow-2xl border border-red-200 animate-fadeIn max-w-xs sm:max-w-md w-full">
            <div className="relative mb-4 w-12 h-12 sm:w-16 sm:h-16 grid place-items-center">
              <span className="absolute inset-0 rounded-full border-4 border-transparent border-r-[#e3342f] animate-spin" />
            </div>
            <span className="text-base sm:text-lg font-bold text-slate-800 text-center uppercase tracking-wider">
              Rejecting Proof...
            </span>
          </div>
        </div>
      )}

      {/* Screenshot Modal */}
      {showScreenshotModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden max-w-2xl w-full animate-zoomIn">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-bold text-slate-800 flex items-center gap-2">
                <FaCamera className="text-blue-500" /> PROOF PREVIEW
              </h3>
              <button
                className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 hover:bg-rose-50 hover:text-rose-600 transition-colors"
                onClick={() => setShowScreenshotModal(false)}
              >
                &times;
              </button>
            </div>
            <div className="p-2 sm:p-4 bg-slate-50">
              <img
                src={currentScreenshot}
                className="max-h-[70vh] w-full object-contain rounded-xl shadow-lg"
                alt="Proof"
              />
            </div>
          </div>
        </div>
      )}

      {/* Header Section */}
      <div className="max-w-7xl mx-auto py-4 sm:py-8 px-2 sm:px-4 lg:px-8">
        <div className="bg-gradient-to-br from-blue-700 via-indigo-800 to-violet-900 rounded-[2rem] shadow-2xl p-4 sm:p-8 text-white relative overflow-hidden mb-6 sm:mb-8">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -mr-32 -mt-32" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-400/10 rounded-full blur-3xl -ml-32 -mb-32" />

          <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6 sm:gap-8">
            <div className="space-y-4">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="p-3 sm:p-4 bg-white/15 rounded-2xl backdrop-blur-md shadow-inner">
                  <FaPlay className="text-yellow-400 text-xl sm:text-3xl animate-pulse" />
                </div>
                <div>
                  <h1 className="text-2xl sm:text-4xl font-black tracking-tight leading-none">FREE RUNNING ADS</h1>
                  <p className="text-blue-200/80 text-[10px] sm:text-sm font-bold uppercase mt-2 tracking-widest">Ongoing Proof Submissions</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 sm:gap-4">
                <div className="px-3 sm:px-4 py-2 bg-black/20 rounded-xl backdrop-blur-sm border border-white/10">
                  <p className="text-[9px] uppercase font-bold text-blue-200/60">Filtered Ads</p>
                  <p className="text-base sm:text-xl font-black">{filteredAds.length}</p>
                </div>
                <div className="px-3 sm:px-4 py-2 bg-black/20 rounded-xl backdrop-blur-sm border border-white/10">
                  <p className="text-[9px] uppercase font-bold text-blue-200/60">Campaigns</p>
                  <p className="text-base sm:text-xl font-black">{groupedAdsArray.length}</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 lg:flex lg:flex-wrap gap-2 sm:gap-3">
              <button
                className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 px-3 sm:px-5 py-2 sm:py-3 rounded-2xl transition-all font-bold text-[10px] sm:text-sm backdrop-blur-sm"
                onClick={() => navigate("/admin/freependingads")}
              >
                <FaClock className="text-yellow-400" /> PENDING
              </button>
              <button
                className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 px-3 sm:px-5 py-2 sm:py-3 rounded-2xl transition-all font-bold text-[10px] sm:text-sm backdrop-blur-sm"
                onClick={() => navigate("/admin/freeapprovedads")}
              >
                <FaCheck className="text-emerald-400" /> APPROVED
              </button>
              <button
                className="col-span-2 lg:col-span-1 flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 px-3 sm:px-5 py-2 sm:py-3 rounded-2xl transition-all font-bold text-[10px] sm:text-sm backdrop-blur-sm"
                onClick={() => navigate("/admin/freecompletedads")}
              >
                <FaCheck className="text-blue-400" /> COMPLETED
              </button>
            </div>
          </div>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white rounded-3xl shadow-xl p-4 sm:p-6 mb-6 sm:mb-8 border border-slate-100 sticky top-4 z-40">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1 relative group">
              <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 transition-colors group-focus-within:text-blue-500" />
              <input
                type="text"
                className="w-full pl-12 pr-4 py-3 sm:py-4 bg-slate-50 border-0 rounded-2xl focus:ring-4 focus:ring-blue-500/10 transition-all font-medium text-slate-700 text-sm sm:text-base placeholder:text-slate-400"
                placeholder="Search Ad ID, Reporter ID, Channel Name..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex items-center justify-between sm:justify-end gap-2 px-1">
              <div className="bg-blue-50 text-blue-700 px-3 py-2 rounded-xl text-[10px] sm:text-xs font-black uppercase tracking-tighter">
                Page {currentPage}/{totalPages}
              </div>
              <div className="bg-emerald-50 text-emerald-700 px-3 py-2 rounded-xl text-[10px] sm:text-xs font-black uppercase tracking-tighter">
                {filteredAds.length} RESULTS
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        {loading ? (
          <div className="flex flex-col items-center justify-center h-64 sm:h-96 gap-6">
            <div className="relative w-16 sm:w-20 h-16 sm:h-20">
              <div className="absolute inset-0 rounded-full border-[6px] border-slate-100" />
              <div className="absolute inset-0 rounded-full border-[6px] border-transparent border-t-blue-600 animate-spin" />
            </div>
            <p className="font-bold text-slate-400 uppercase tracking-widest text-[10px] sm:text-xs">Initialising campaign data...</p>
          </div>
        ) : filteredAds.length === 0 ? (
          <div className="bg-white rounded-[2.5rem] shadow-2xl p-8 sm:p-20 text-center border border-slate-100 max-w-2xl mx-auto mt-12">
            <div className="w-24 h-24 mx-auto mb-8 bg-slate-50 rounded-full flex items-center justify-center">
              <FaPlay className="w-10 h-10 text-slate-300" />
            </div>
            <h3 className="text-xl sm:text-3xl font-black text-slate-800 mb-4 tracking-tight">NO RUNNING ADS FOUND</h3>
            <p className="text-slate-500 font-medium mb-10 text-sm sm:text-base px-6">We couldn't find any advertisements currently in the running state matching your criteria.</p>
            <button
              onClick={() => setSearchTerm("")}
              className="bg-slate-800 text-white px-8 py-4 rounded-2xl font-black text-sm hover:scale-105 active:scale-95 transition-all shadow-xl shadow-slate-200"
            >
              RESET FILTERS
            </button>
          </div>
        ) : (
          <div className="space-y-6 sm:space-y-12">
            {paginatedAds.map((groupedAd, index) => (
              <div key={groupedAd.adId || index} className="bg-white rounded-[2rem] sm:rounded-[2.5rem] shadow-2xl border border-slate-100 overflow-hidden group/card hover:border-blue-500/30 transition-colors">
                {/* Ad Header Card */}
                <div className="bg-slate-50/50 p-4 sm:p-8 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
                  <div className="flex items-center gap-4 sm:gap-6">
                    <div className="w-16 sm:w-20 h-16 sm:h-20 bg-blue-600 rounded-3xl flex items-center justify-center text-white font-black text-xl sm:text-3xl shadow-lg shadow-blue-200 group-hover/card:scale-110 transition-transform">
                      {groupedAd.adId}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                        <span className="font-black text-slate-800 uppercase text-[10px] sm:text-xs tracking-widest">CAMPAIGN ACTIVE</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="bg-white px-3 py-1.5 rounded-xl border border-slate-100 flex flex-col">
                          <span className="text-[8px] font-bold text-slate-400 uppercase tracking-tighter">Required Slots</span>
                          <span className="text-base font-black text-slate-800">{groupedAd.requiredReporterCount}</span>
                        </div>
                        <div className="bg-white px-3 py-1.5 rounded-xl border border-slate-100 flex flex-col">
                          <span className="text-[8px] font-bold text-slate-400 uppercase tracking-tighter">Active Users</span>
                          <span className="text-base font-black text-blue-600">{groupedAd.reporters.length}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Submissions List */}
                <div className="divide-y divide-slate-50">
                  {/* Laptop/Desktop Header */}
                  <div className="hidden lg:grid grid-cols-12 gap-4 px-8 py-4 bg-slate-50/30 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">
                    <div className="col-span-2">User details</div>
                    <div className="col-span-2">Target/Platform</div>
                    <div className="col-span-2">Channel Info</div>
                    <div className="col-span-2">Links & Info</div>
                    <div className="col-span-2">Proof Submission</div>
                    <div className="col-span-2 text-right">Administrative Actions</div>
                  </div>

                  {groupedAd.reporters.map((reporter, rIdx) => (
                    <div key={`${groupedAd.adId}-${reporter.reporterId}-${rIdx}`} className="p-4 sm:p-8 hover:bg-slate-50/50 transition-colors">
                      {/* Mobile Card Layout */}
                      <div className="lg:hidden space-y-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-slate-100 rounded-2xl flex items-center justify-center font-black text-blue-600 text-sm">
                              {reporter.iinsafId?.charAt(0) || <FaUser className="text-slate-400" />}
                            </div>
                            <div>
                              <p className="font-black text-slate-800 text-sm">{reporter.iinsafId}</p>
                              <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-tighter ${reporter.iinsafId?.startsWith('INF') ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                                {reporter.iinsafId?.startsWith('INF') ? 'Influencer' : 'Reporter'}
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-[9px] font-bold text-slate-400 uppercase">Platform</p>
                            <p className="text-xs font-bold text-slate-800">{reporter.platform || 'N/A'}</p>
                          </div>
                        </div>

                        <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm space-y-3">
                          <div className="flex justify-between items-center text-xs">
                            <span className="font-bold text-slate-500 uppercase text-[10px]">Channel</span>
                            <span className="font-black text-slate-800 truncate max-w-[150px]">{reporter.channelName || 'N/A'}</span>
                          </div>
                          <div className="flex justify-between items-center text-xs">
                            <span className="font-bold text-slate-500 uppercase text-[10px]">Duration</span>
                            <span className="font-black text-slate-800">{reporter.duration || 'N/A'}</span>
                          </div>
                        </div>

                        <div className="flex gap-2">
                          {reporter.videoLink && (
                            <a href={reporter.videoLink} target="_blank" rel="noreferrer" className="flex-1 py-3 bg-blue-100 text-blue-700 rounded-xl flex items-center justify-center gap-2 font-black text-xs">
                              <FaPlay className="text-[10px]" /> VIDEO
                            </a>
                          )}
                          {reporter.screenshot && (
                            <button
                              onClick={() => {
                                const url = reporter.screenshot.startsWith('http') ? reporter.screenshot : `${process.env.REACT_APP_API_BASE_URL}/${reporter.screenshot}`;
                                setCurrentScreenshot(url);
                                setShowScreenshotModal(true);
                              }}
                              className="flex-1 py-3 bg-emerald-100 text-emerald-700 rounded-xl flex items-center justify-center gap-2 font-black text-xs"
                            >
                              <FaCamera className="text-[10px]" /> PROOF
                            </button>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-3 pt-2">
                          <button onClick={() => handleAcceptSubmit(groupedAd.adId, reporter.reporterId)} className="py-4 bg-emerald-500 text-white rounded-2xl font-black text-xs shadow-lg shadow-emerald-100 uppercase tracking-widest">
                            ACCEPT
                          </button>
                          <button onClick={() => { setRejectTarget({ adId: groupedAd.adId, reporterId: reporter.reporterId }); setShowRejectModal(true); }} className="py-4 bg-rose-500 text-white rounded-2xl font-black text-xs shadow-lg shadow-rose-100 uppercase tracking-widest">
                            REJECT
                          </button>
                        </div>
                      </div>

                      {/* Desktop Grid Layout */}
                      <div className="hidden lg:grid grid-cols-12 gap-4 items-center">
                        <div className="col-span-2">
                          <p className="font-black text-slate-800 text-sm tracking-tight">{reporter.iinsafId}</p>
                          <span className={`text-[10px] px-2 py-0.5 rounded-lg font-bold uppercase mt-1 inline-block ${reporter.iinsafId?.startsWith('INF') ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                            {reporter.iinsafId?.startsWith('INF') ? 'Influencer' : 'Reporter'}
                          </span>
                        </div>
                        <div className="col-span-2">
                          <p className="text-xs font-black text-slate-800 uppercase tracking-tighter">{reporter.platform || 'N/A'}</p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-sm font-bold text-slate-800 truncate">{reporter.channelName || 'N/A'}</p>
                        </div>
                        <div className="col-span-2 space-y-1">
                          {reporter.videoLink && (
                            <a href={reporter.videoLink} target="_blank" rel="noreferrer" className="text-blue-600 flex items-center font-bold text-xs gap-1.5 hover:underline">
                              <FaPlay className="text-[10px]" /> View Link
                            </a>
                          )}
                          <p className="text-xs text-slate-500 font-medium">Len: <span className="text-slate-800 font-bold">{reporter.duration || 'N/A'}</span></p>
                        </div>
                        <div className="col-span-2">
                          {reporter.screenshot ? (
                            <button
                              onClick={() => {
                                const url = reporter.screenshot.startsWith('http') ? reporter.screenshot : `${process.env.REACT_APP_API_BASE_URL}/${reporter.screenshot}`;
                                setCurrentScreenshot(url);
                                setShowScreenshotModal(true);
                              }}
                              className="px-4 py-2 bg-slate-100 hover:bg-blue-600 hover:text-white rounded-xl transition-all font-bold text-xs flex items-center gap-2 group/btn"
                            >
                              <FaCamera className="text-slate-400 group-hover/btn:text-white" /> VIEW PROOF
                            </button>
                          ) : <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest">NO PROOF</span>}
                        </div>
                        <div className="col-span-2 text-right space-x-2 flex justify-end">
                          <button onClick={() => handleAcceptSubmit(groupedAd.adId, reporter.reporterId)} className="w-10 h-10 bg-emerald-50 text-emerald-600 rounded-xl hover:bg-emerald-500 hover:text-white transition-all flex items-center justify-center shadow-sm">
                            <FaCheck />
                          </button>
                          <button onClick={() => { setRejectTarget({ adId: groupedAd.adId, reporterId: reporter.reporterId }); setShowRejectModal(true); }} className="w-10 h-10 bg-rose-50 text-rose-600 rounded-xl hover:bg-rose-500 hover:text-white transition-all flex items-center justify-center shadow-sm">
                            <FaTimes />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center mt-12 mb-20">
                <nav className="inline-flex items-center space-x-2 bg-white rounded-3xl shadow-2xl p-3 border border-slate-100">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="w-10 sm:w-12 h-10 sm:h-12 flex items-center justify-center rounded-2xl bg-slate-50 text-slate-400 disabled:opacity-50 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                  >
                    &larr;
                  </button>
                  <div className="flex items-center gap-1.5 px-2">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`w-10 sm:w-12 h-10 sm:h-12 flex items-center justify-center rounded-2xl font-black text-xs sm:text-sm transition-all ${currentPage === pageNum ? 'bg-blue-600 text-white shadow-xl shadow-blue-200' : 'text-slate-400 hover:bg-slate-100'}`}
                      >
                        {pageNum}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="w-10 sm:w-12 h-10 sm:h-12 flex items-center justify-center rounded-2xl bg-slate-50 text-slate-400 disabled:opacity-50 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                  >
                    &rarr;
                  </button>
                </nav>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Reject Reason Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
          <div className="bg-white rounded-[2.5rem] shadow-2xl p-6 sm:p-10 w-full max-w-lg border border-slate-100 animate-zoomIn overflow-hidden relative">
            <div className="absolute top-0 right-0 w-32 h-32 bg-rose-50 rounded-full -mr-16 -mt-16" />

            <div className="relative z-10">
              <div className="w-16 h-16 bg-rose-100 rounded-3xl flex items-center justify-center text-rose-600 mb-6 font-black text-2xl">!</div>
              <h2 className="text-2xl sm:text-3xl font-black text-slate-800 mb-2 tracking-tight">REJECT PROOF</h2>
              <p className="text-slate-500 font-medium mb-8 text-sm sm:text-base">Explain why this submission did not meet the advertisement requirements.</p>

              <div className="space-y-6">
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1 mb-2 block">REASON FOR REJECTION</label>
                  <textarea
                    className="w-full bg-slate-50 border-0 rounded-2xl p-4 sm:p-6 min-h-[140px] focus:ring-4 focus:ring-rose-500/10 transition-all font-medium text-slate-700 shadow-inner"
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="E.g. Video duration matches requirements but audio quality is poor..."
                    disabled={rejectLoading}
                  />
                </div>

                <div className="flex gap-3 mt-4 sm:mt-0">
                  <button
                    className="flex-1 py-4 bg-slate-100 text-slate-600 rounded-2xl font-black text-xs hover:bg-slate-200 transition-colors"
                    onClick={() => { setShowRejectModal(false); setRejectReason(""); setRejectTarget(null); }}
                    disabled={rejectLoading}
                  >
                    CANCEL
                  </button>
                  <button
                    className="flex-[2] py-4 bg-rose-600 text-white rounded-2xl font-black text-xs shadow-xl shadow-rose-200 hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50 disabled:hover:scale-100 tracking-widest"
                    onClick={handleRejectSubmit}
                    disabled={!rejectReason.trim() || rejectLoading}
                  >
                    {rejectLoading ? "PROCESSING..." : "CONFIRM REJECTION"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Media Preview Modal */}
      {previewMedia && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[70] p-4 sm:p-8" onClick={closeMediaPreview}>
          <div className="relative max-w-5xl w-full h-full sm:h-auto flex items-center justify-center" onClick={e => e.stopPropagation()}>
            <button
              className="absolute -top-12 sm:top-0 -right-2 sm:-right-16 w-12 h-12 bg-white/10 hover:bg-rose-500 text-white rounded-2xl flex items-center justify-center text-2xl transition-all"
              onClick={closeMediaPreview}
            >
              &times;
            </button>
            {previewMedia.type === 'video' ? (
              <video
                src={previewMedia.url}
                className="max-h-[85vh] w-auto max-w-full rounded-2xl shadow-3xl"
                controls
                autoPlay
              />
            ) : (
              <img
                src={previewMedia.url}
                alt="Preview"
                className="max-h-[85vh] w-auto max-w-full rounded-2xl shadow-3xl object-contain"
              />
            )}
          </div>
        </div>
      )}

      <ToastContainer position="bottom-center" autoClose={3000} theme="dark" />
    </React.Fragment>
  );
};

export default FreeRunningAds;
